<template>
  <div class="comment-section">
    <div class="comment-header">
      <span class="comment-count">{{ comments.length }} {{ $t('comments') }}</span>
    </div>

    <!-- Top-level comment input -->
    <div class="comment-input-box">
      <div class="comment-input-avatar">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"/></svg>
      </div>
      <div class="comment-input-body">
        <input v-model="newUsername" class="comment-username-input" :placeholder="$t('comment_username_placeholder')" maxlength="28" />
        <textarea
          v-model="newContent"
          class="comment-textarea"
          :placeholder="$t('write_comment')"
          rows="2"
          maxlength="2000"
        />
        <div class="comment-input-actions">
          <span class="text-tertiary text-xs">{{ newContent.length }}/2000</span>
          <button class="btn btn-sm btn-primary" @click="submitComment" :disabled="!canSubmit || submitting">
            {{ submitting ? '...' : $t('comment_submit') }}
          </button>
        </div>
        <p v-if="commentError" class="comment-error">{{ commentError }}</p>
      </div>
    </div>

    <div v-if="loading" class="text-tertiary text-sm text-center mt-2">Loading...</div>

    <!-- Comment list -->
    <div v-if="!loading && comments.length === 0" class="text-tertiary text-sm text-center mt-2">
      {{ $t('no_comments') }}
    </div>

    <div v-else class="comments-list">
      <CommentItem
        v-for="comment in comments"
        :key="comment.id"
        :comment="comment"
        :depth="0"
        :postId="postId"
        @reply="onReply"
        @refresh="fetchComments"
      />
    </div>
  </div>
</template>

<script>
import { getComments, createComment, replyComment, checkUsernameOnPost } from '../api'
import CommentItem from './CommentItem.vue'

function getClientToken() {
  let token = localStorage.getItem('markjy_client_token')
  if (!token) {
    token = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2)
    localStorage.setItem('markjy_client_token', token)
  }
  return token
}

export default {
  name: 'CommentSection',
  components: { CommentItem },
  props: {
    postId: { type: Number, required: true }
  },
  data() {
    return {
      comments: [],
      loading: true,
      newUsername: localStorage.getItem('markjy_comment_username') || '',
      newContent: '',
      submitting: false,
      commentError: ''
    }
  },
  computed: {
    canSubmit() {
      return this.newUsername.trim().length > 0 && this.newContent.trim().length > 0
    }
  },
  methods: {
    fetchComments() {
      getComments(this.postId).then(res => {
        this.comments = res.data.data || []
      }).finally(() => {
        this.loading = false
      })
    },
    async submitComment() {
      if (!this.canSubmit) return
      this.commentError = ''
      this.submitting = true

      const username = this.newUsername.trim()
      const content = this.newContent.trim()

      try {
        // Check username availability on this post
        const checkRes = await checkUsernameOnPost(this.postId, username)
        if (!checkRes.data.data.available) {
          this.commentError = checkRes.data.data.reason
          this.submitting = false
          return
        }

        await createComment(this.postId, username, content)
        localStorage.setItem('markjy_comment_username', username)
        this.newContent = ''
        this.commentError = ''
        this.fetchComments()
      } catch (err) {
        const msg = err.response && err.response.data ? err.response.data.message : 'Network error'
        this.commentError = msg
      } finally {
        this.submitting = false
      }
    },
    onReply() {
      this.fetchComments()
    }
  },
  mounted() {
    this.fetchComments()
  }
}
</script>

<style scoped>
.comment-section {
  margin-top: 24px;
}
.comment-header {
  margin-bottom: 20px;
}
.comment-count {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text-secondary);
}
.comment-input-box {
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border-light);
}
.comment-input-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
}
.comment-input-body {
  flex: 1;
  min-width: 0;
}
.comment-username-input {
  width: 100%;
  padding: 8px 0;
  border: none;
  border-bottom: 1px solid var(--border-light);
  background: transparent;
  color: var(--text-primary);
  font-size: 0.85rem;
  font-family: inherit;
  outline: none;
  margin-bottom: 8px;
}
.comment-username-input::placeholder {
  color: var(--text-tertiary);
}
.comment-textarea {
  width: 100%;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.9rem;
  font-family: inherit;
  resize: none;
  outline: none;
  line-height: 1.6;
  padding: 4px 0;
}
.comment-textarea::placeholder {
  color: var(--text-tertiary);
}
.comment-input-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}
.comment-error {
  color: #d32f2f;
  font-size: 0.8rem;
  margin-top: 6px;
}
.comments-list {
  display: flex;
  flex-direction: column;
}
</style>
